import React from 'react';
import { Box, IconButton, Menu, Tooltip, Typography } from '@mui/material';
import { AppsRounded } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAppTheme } from '../../theme';
import { useNavItems, isModifiedClick } from './navConfig';

/**
 * The apps grid — a fast jump to any business module the tenant actually owns.
 *
 * It renders from `useNavItems()`, the same role- and entitlement-filtered list
 * the rail uses, so a module the tenant has not purchased can never appear here
 * either. There is no second hard-coded module list to drift out of sync.
 */
const AppsGridMenu: React.FC = () => {
  const { colors } = useAppTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const items = useNavItems();

  const apps = items.filter((i) => i.section === 'modules');
  // Nothing bought, nothing to show — a grid button opening an empty sheet is
  // a dead button.
  if (apps.length === 0) return null;

  return (
    <>
      <Tooltip title="Modules">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="open module grid"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          sx={{ color: colors.textSecondary }}
        >
          <AppsRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, borderRadius: 2, width: 328, p: 1 } }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        <Typography
          variant="caption"
          sx={{ display: 'block', px: 1, py: 0.75, fontWeight: 700, color: colors.textSecondary }}
        >
          Your modules
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0.5,
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          {apps.map((app) => (
            <Box
              key={app.text}
              component={RouterLink}
              to={app.path}
              onClick={(e: React.MouseEvent) => {
                if (isModifiedClick(e)) return;
                setAnchorEl(null);
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.75,
                p: 1.25,
                borderRadius: 2,
                textDecoration: 'none',
                color: colors.textPrimary,
                textAlign: 'center',
                '&:hover': { backgroundColor: colors.background },
                '&:focus-visible': { outline: `2px solid ${colors.primary}`, outlineOffset: -2 },
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 34,
                  height: 34,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 2,
                  color: colors.primary,
                  backgroundColor: colors.backgroundAlt,
                  '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
                }}
              >
                {app.icon}
              </Box>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, lineHeight: 1.25 }}>
                {app.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Menu>
    </>
  );
};

export default AppsGridMenu;
